import { useMemo } from 'react';
import { useSnackbar } from 'notistack';
import {
  QueryCache,
  useMutation,
  QueryClient,
  useQueryClient,
  UseMutationResult,
  MutationCache,
  Query
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { axiosHelper } from '@setvi/shared/services';
import { AxiosMethods, CommonApi } from '@setvi/shared/enums';
import { SlackAPIErrorBody } from '@setvi/shared/services/react-query/mutation/common/types';

const ignoreErrorHandler = (error: AxiosError<any>) => {
  if (error?.code === 'ERR_CANCELED') return true;
  if (!error?.config) return false;

  const { baseURL } = error?.config;
  const status = error?.status;

  const apis = [
    process.env.REVOPS_API,
    process.env.REVOPS_INTELLIGENCE_API,
    process.env.INTELLIGENCE_EXPERIMENTAL_API
  ];
  if (status === 404 && apis.includes(baseURL)) return true;

  return false;
};

const extractErrorMessage = (error: AxiosError<any>) => {
  if (!error?.response) return 'Something went wrong, please try again later';

  const data =
    error?.response?.data?.message ||
    error?.response?.data?.Message ||
    error?.response?.data;

  if (data && typeof data === 'string') return data;

  if (typeof data?.errors === 'object') {
    const errorKey = Object.keys(data.errors)[0];
    return data.errors[errorKey][0];
  }

  if (
    (data?.Error && typeof data?.Error === 'string') ||
    (data?.error && typeof data?.error === 'string')
  )
    return data?.Error || data?.error;

  if (
    (data?.Error?.Message && typeof data?.Error?.Message === 'string') ||
    (data?.error?.message && typeof data?.error?.message === 'string')
  )
    return data?.Error?.Message || data?.error?.message;

  return error?.message || 'Something went wrong, please try again later';
};

const sendAPIErrorToSlackChannel = async (errorData: SlackAPIErrorBody) => {
  try {
    await axiosHelper({
      endpoint: CommonApi.SlackChannelAPIError,
      method: AxiosMethods.POST,
      baseURL: process.env.REVOPS_INTELLIGENCE_API,
      data: errorData
    });
  } catch {
    // Invalid API
  }
};

const tranformError = (error: AxiosError<any>) => {
  const localUser = localStorage?.getItem('user');
  let user = null;

  if (localUser) {
    try {
      const parsedUser = JSON.parse(localUser);
      if (parsedUser) {
        user = {
          email: parsedUser.Email,
          userId: parsedUser?.ID,
          company: parsedUser?.CompanyID?.toString(),
          fullName: parsedUser?.FullName
        };
      }
    } catch {
      // Invalid JSON in localStorage, continue with user = null
    }
  }

  return {
    baseUrl: error?.config?.baseURL || '',
    code: error?.code || '',
    method: error?.config?.method || '',
    params: JSON.stringify(error?.config?.params) || '',
    endpoint: error?.config?.url || '',
    body: error?.config?.data || '',
    error: extractErrorMessage(error),
    user
  };
};

export function useReactQuery() {
  const { enqueueSnackbar } = useSnackbar();

  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false
          }
        },
        queryCache: new QueryCache({
          onError: (error: AxiosError, query: Query) => {
            const body = tranformError(error);
            sendAPIErrorToSlackChannel(body);

            if (ignoreErrorHandler(error)) return;
            enqueueSnackbar(extractErrorMessage(error), {
              variant: 'error',
              autoHideDuration: 8000
            });

            // expect error
            // eslint-disable-next-line no-console
            console.error(query);
          }
        }),
        mutationCache: new MutationCache({
          onError: (error: AxiosError) => {
            const body = tranformError(error);
            sendAPIErrorToSlackChannel(body);

            if (ignoreErrorHandler(error)) return;
            enqueueSnackbar(extractErrorMessage(error), {
              variant: 'error',
              autoHideDuration: 8000
            });
          }
        })
      }),
    [enqueueSnackbar]
  );

  return {
    queryClient
  };
}

export function useSubscribedMutation(
  props: any,
  queries?: string[]
): UseMutationResult<any> {
  const queryClient = useQueryClient();

  return useMutation({
    ...props,
    onSuccess: () => {
      queries?.forEach((query: string) => {
        queryClient.invalidateQueries({
          queryKey: [query]
        });
      });
    }
  });
}
